import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../axiosInstance";

const emptyRow = () => ({
  student_admission_number: "",
  parent_first_name: "",
  parent_middle_name: "",
  parent_last_name: "",
  parent_email: "",
  parent_phone: "",
  relationship: "GUARDIAN",
  is_primary_contact: false,
});

const columns = [
  "student_admission_number",
  "parent_first_name",
  "parent_middle_name",
  "parent_last_name",
  "parent_email",
  "parent_phone",
  "relationship",
  "is_primary_contact",
];

function parseCsvLine(line) {
  const values = [];
  let current = "";
  let quoted = false;
  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    if (char === '"') {
      if (quoted && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        quoted = !quoted;
      }
    } else if (char === "," && !quoted) {
      values.push(current);
      current = "";
    } else {
      current += char;
    }
  }
  values.push(current);
  return values.map((value) => value.trim());
}

function csvEscape(value) {
  const text = String(value ?? "");
  return /[",\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
}

function downloadCsv(filename, rows) {
  const csv = rows.map((row) => row.map(csvEscape).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function statusBadge(status) {
  if (status === "NEW_ACCOUNT" || status === "CREATED") return "text-bg-success";
  if (status === "EXISTING_PARENT" || status === "LINKED") return "text-bg-info";
  if (status === "ALREADY_LINKED") return "text-bg-secondary";
  return "text-bg-danger";
}

function BulkParentAccountsModal({ onClose, onCompleted }) {
  const [rows, setRows] = useState([emptyRow()]);
  const [preview, setPreview] = useState(null);
  const [results, setResults] = useState(null);
  const [working, setWorking] = useState(false);

  const canCommit = Boolean(preview?.can_commit);
  const rowCount = useMemo(() => rows.filter((row) => Object.values(row).some((value) => String(value ?? "").trim())).length, [rows]);

  const updateRow = (index, field, value) => {
    setRows((prev) => prev.map((row, rowIndex) => rowIndex === index ? { ...row, [field]: value } : row));
    setPreview(null);
    setResults(null);
  };

  const addRow = () => setRows((prev) => [...prev, emptyRow()]);
  const removeRow = (index) => setRows((prev) => prev.length === 1 ? [emptyRow()] : prev.filter((_, rowIndex) => rowIndex !== index));

  const downloadTemplate = () => {
    downloadCsv("Grange_Bulk_Parent_Accounts_Template.csv", [
      columns,
      ["TGCS0000001BT", "Parent", "", "Surname", "parent@example.com", "+26377XXXXXXX", "MOTHER", "TRUE"],
    ]);
  };

  const importCsv = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const lines = text.split(/\r?\n/).filter((line) => line.trim());
      if (lines.length < 2) throw new Error("CSV has no data rows.");
      const header = parseCsvLine(lines[0]).map((value) => value.toLowerCase());
      const missing = columns.filter((column) => !header.includes(column));
      if (missing.length) throw new Error(`Missing columns: ${missing.join(", ")}`);
      const imported = lines.slice(1).map((line) => {
        const values = parseCsvLine(line);
        const row = emptyRow();
        header.forEach((name, index) => {
          if (!columns.includes(name)) return;
          if (name === "is_primary_contact") {
            row[name] = ["1", "true", "yes", "y"].includes(String(values[index] || "").toLowerCase());
          } else {
            row[name] = values[index] || "";
          }
        });
        return row;
      });
      setRows(imported.length ? imported : [emptyRow()]);
      setPreview(null);
      setResults(null);
      toast.success(`${imported.length} row(s) imported.`);
    } catch (error) {
      toast.error(error.message || "Failed to import CSV.");
    } finally {
      event.target.value = "";
    }
  };

  const previewBatch = async () => {
    try {
      setWorking(true);
      const response = await axiosInstance.post("parents/bulk/preview/", { rows });
      setPreview(response.data);
      setResults(null);
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || "Failed to preview parent accounts.");
    } finally {
      setWorking(false);
    }
  };

  const commitBatch = async () => {
    if (!canCommit) return;
    if (!window.confirm(`Create/link parent accounts for ${rowCount} row(s)?`)) return;
    try {
      setWorking(true);
      const response = await axiosInstance.post("parents/bulk/commit/", { rows });
      setResults(response.data);
      setPreview(null);
      toast.success(response.data.message || "Bulk parent accounts completed.");
      if (onCompleted) onCompleted();
    } catch (error) {
      console.error(error);
      if (error.response?.data?.rows) setPreview({ rows: error.response.data.rows, can_commit: false, counts: {} });
      toast.error(error.response?.data?.detail || "Failed to create parent accounts.");
    } finally {
      setWorking(false);
    }
  };

  const downloadCredentials = () => {
    if (!results?.results?.length) return;
    downloadCsv("Grange_Parent_Login_Credentials.csv", [
      ["status", "student_admission_number", "student_name", "parent_name", "email", "username", "temporary_password", "relationship"],
      ...results.results.map((item) => [item.status, item.student_admission_number, item.student_name, item.parent_name, item.email, item.username, item.temporary_password || "EXISTING ACCOUNT - PASSWORD UNCHANGED", item.relationship]),
    ]);
  };

  return (
    <div className="modal fade show d-block" tabIndex="-1" style={{ background: "rgba(0,0,0,.45)" }}>
      <div className="modal-dialog modal-xl modal-dialog-scrollable">
        <div className="modal-content border-0 shadow">
          <div className="modal-header">
            <div>
              <h5 className="modal-title fw-bold">Bulk Parent Accounts</h5>
              <small className="text-muted">Create missing parent accounts, reuse existing parents, and link children in one controlled batch.</small>
            </div>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>

          <div className="modal-body">
            <div className="alert alert-light border d-flex flex-wrap justify-content-between align-items-center gap-2">
              <div>
                <strong>Matching rule:</strong> existing parents are reused by email or phone. If email is blank, the school generates a local login email from the phone number.
              </div>
              <div className="d-flex flex-wrap gap-2">
                <button className="btn btn-outline-success btn-sm" onClick={downloadTemplate}><i className="bi bi-download me-2"></i>CSV Template</button>
                <label className="btn btn-outline-primary btn-sm mb-0"><i className="bi bi-upload me-2"></i>Import CSV<input type="file" accept=".csv,text/csv" className="d-none" onChange={importCsv} /></label>
                <button className="btn btn-light border btn-sm" onClick={addRow}><i className="bi bi-plus-lg me-2"></i>Add Row</button>
              </div>
            </div>

            {!results && (
              <div className="table-responsive border rounded-3">
                <table className="table table-sm align-middle mb-0" style={{ minWidth: 1280 }}>
                  <thead className="table-light">
                    <tr><th>Student Admission</th><th>First Name</th><th>Middle</th><th>Last Name</th><th>Email</th><th>Phone</th><th>Relationship</th><th>Primary</th><th></th></tr>
                  </thead>
                  <tbody>
                    {rows.map((row, index) => (
                      <tr key={index}>
                        <td><input className="form-control form-control-sm" value={row.student_admission_number} onChange={(e) => updateRow(index, "student_admission_number", e.target.value)} /></td>
                        <td><input className="form-control form-control-sm" value={row.parent_first_name} onChange={(e) => updateRow(index, "parent_first_name", e.target.value)} /></td>
                        <td><input className="form-control form-control-sm" value={row.parent_middle_name} onChange={(e) => updateRow(index, "parent_middle_name", e.target.value)} /></td>
                        <td><input className="form-control form-control-sm" value={row.parent_last_name} onChange={(e) => updateRow(index, "parent_last_name", e.target.value)} /></td>
                        <td><input className="form-control form-control-sm" type="email" value={row.parent_email} onChange={(e) => updateRow(index, "parent_email", e.target.value)} /></td>
                        <td><input className="form-control form-control-sm" value={row.parent_phone} onChange={(e) => updateRow(index, "parent_phone", e.target.value)} /></td>
                        <td><select className="form-select form-select-sm" value={row.relationship} onChange={(e) => updateRow(index, "relationship", e.target.value)}><option value="FATHER">Father</option><option value="MOTHER">Mother</option><option value="GUARDIAN">Guardian</option><option value="SPONSOR">Sponsor</option><option value="OTHER">Other</option></select></td>
                        <td className="text-center"><input className="form-check-input" type="checkbox" checked={Boolean(row.is_primary_contact)} onChange={(e) => updateRow(index, "is_primary_contact", e.target.checked)} /></td>
                        <td><button className="btn btn-sm btn-light border text-danger" onClick={() => removeRow(index)} title="Remove row"><i className="bi bi-trash"></i></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {preview?.rows && (
              <div className="mt-4">
                <div className={`alert ${preview.can_commit ? "alert-success" : "alert-warning"}`}>
                  {preview.can_commit ? "Preview passed. This batch is ready to commit." : "Some rows need attention before this batch can be committed."}
                </div>
                <div className="table-responsive border rounded-3">
                  <table className="table table-sm align-middle mb-0">
                    <thead className="table-light"><tr><th>Row</th><th>Student</th><th>Parent</th><th>Status</th><th>Message</th></tr></thead>
                    <tbody>{preview.rows.map((item) => <tr key={item.row}><td>{item.row}</td><td><div className="fw-semibold">{item.student_name || item.student_admission_number}</div><small className="text-muted">{item.student_admission_number} {item.student_class ? `· ${item.student_class}` : ""}</small></td><td>{item.matched_parent || `${item.parent_first_name || ""} ${item.parent_last_name || ""}`.trim()}<br/><small className="text-muted">{item.matched_email || item.parent_email || item.parent_phone}</small></td><td><span className={`badge ${statusBadge(item.status)}`}>{item.status.replaceAll("_", " ")}</span></td><td>{item.message}</td></tr>)}</tbody>
                  </table>
                </div>
              </div>
            )}

            {results?.results && (
              <div>
                <div className="alert alert-success"><strong>Completed.</strong> {results.created_accounts} new account(s), {results.new_links} new existing-parent link(s), {results.already_linked} already linked.</div>
                <div className="table-responsive border rounded-3">
                  <table className="table table-sm align-middle mb-0">
                    <thead className="table-light"><tr><th>Student</th><th>Parent</th><th>Username</th><th>Temporary Password</th><th>Status</th></tr></thead>
                    <tbody>{results.results.map((item) => <tr key={`${item.row}-${item.parent_user_id}`}><td>{item.student_name}<br/><small className="text-muted">{item.student_admission_number}</small></td><td>{item.parent_name}</td><td>{item.username}</td><td><code>{item.temporary_password || "Existing account - unchanged"}</code></td><td><span className={`badge ${statusBadge(item.status)}`}>{item.status}</span></td></tr>)}</tbody>
                  </table>
                </div>
                <button className="btn btn-success mt-3" onClick={downloadCredentials}><i className="bi bi-file-earmark-arrow-down me-2"></i>Download Credentials CSV</button>
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button className="btn btn-light border" onClick={onClose}>Close</button>
            {!results && <button className="btn btn-outline-success" onClick={previewBatch} disabled={working || rowCount === 0}>{working ? "Working..." : "Preview Batch"}</button>}
            {!results && preview && <button className="btn btn-success" onClick={commitBatch} disabled={working || !canCommit}>{working ? "Processing..." : "Create / Link Accounts"}</button>}
          </div>
        </div>
      </div>
    </div>
  );
}

export default BulkParentAccountsModal;
